import * as authApi from '@/api/auth'
import * as userApi from '@/api/user'
import { setSession, removeToken, getToken } from '@/utils/auth'
import { isTokenExpired } from '@/utils/jwt'
import { STORAGE_KEYS } from '@/constants/storage'

const state = {
  user: null,
  isLoggedIn: false,
  sessionReady: false,
}

const getters = {
  isLoggedIn: (state) => state.isLoggedIn,
  sessionReady: (state) => state.sessionReady,
  user: (state) => state.user,
  role: (state) => state.user?.level || localStorage.getItem(STORAGE_KEYS.ROLE) || null,
}

const actions = {
  async bootstrapSession({ commit, dispatch, state }) {
    if (state.sessionReady) return
    const token = getToken()
    if (!token || isTokenExpired(token)) {
      await dispatch('clearSession', { notifyServer: true })
      commit('SET_SESSION_READY', true)
      return
    }
    try {
      await authApi.syncSession()
      const { data } = await userApi.getProfile()
      const user = data.data?.user ?? data.data
      if (!user) {
        throw new Error('missing user')
      }
      commit('SET_USER', user)
    } catch {
      await dispatch('clearSession', { notifyServer: true })
    }
    commit('SET_SESSION_READY', true)
  },

  async clearSession({ commit }, { notifyServer = false } = {}) {
    if (notifyServer) {
      try {
        await authApi.logout()
      } catch (e) {
        // ignore
      }
    }
    removeToken()
    commit('RESET_USER')
    commit('app/SET_FEATURE_SETTINGS', { hide_scheduler: false, hide_ai: false, loaded: false }, { root: true })
  },

  async login({ commit, dispatch }, credentials) {
    const { data } = await authApi.login(credentials)
    const { user, token, refresh_token: refreshToken } = data.data

    setSession({ token, refreshToken, role: user.level })
    commit('SET_USER', user)
    await dispatch('app/fetchFeatureSettings', null, { root: true })
    return data
  },

  async register(_, payload) {
    const { data } = await authApi.register(payload)
    return data
  },

  async completeGoogle({ commit, dispatch }, { ticket }) {
    const { data } = await authApi.completeGoogle({ ticket })
    const { user, token, refresh_token: refreshToken } = data.data

    setSession({ token, refreshToken, role: user.level })
    commit('SET_USER', user)
    await dispatch('app/fetchFeatureSettings', null, { root: true })
    return data
  },

  async logout({ dispatch }) {
    await dispatch('clearSession', { notifyServer: true })
  },
}

const mutations = {
  SET_USER(state, user) {
    state.user = user
    state.isLoggedIn = true
  },
  RESET_USER(state) {
    state.user = null
    state.isLoggedIn = false
  },
  SET_SESSION_READY(state, ready) {
    state.sessionReady = ready
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
